import {
  SectionMessage,
  SelectItem,
  TextArea,
  Link,
  Stack,
  Select,
  Divider,
} from "@certn/cascadia-react";
import { useTranslation, Trans } from "react-i18next";
import { Dispatch, SetStateAction, useContext } from "react";
import { CascadiaJsonFormContext } from "@certn/forms/src/CascadiaJsonFormContext";
import { GapActivityType } from "./types";
import { isGapStatementActivity } from "./utils";

interface GapStatementProps {
  reason?: string;
  index: number;
  onReasonChange: (reason: string) => void;
  onNavigateToSection: (section: string) => void;
  activityType: GapActivityType;
  setActivityType: Dispatch<SetStateAction<GapActivityType>>;
  includesEducation?: boolean;
}

export function GapStatement({
  reason = "",
  index,
  onReasonChange,
  onNavigateToSection,
  setActivityType,
  activityType,
  includesEducation = true,
}: GapStatementProps) {
  const { t } = useTranslation();
  const { submitErrors } = useContext(CascadiaJsonFormContext);

  const testID = `gap-${index + 1}`;
  const errors = submitErrors[`gap_history.gaps.${index}.reason`] ?? [];

  const gapActivityOptions = [
    { value: "WORKING", label: t("Working in a job or contract") },
    { value: "STUDYING", label: t("Studying or training") },
    {
      value: "OTHER",
      label: t("Other (for example, leave, travel, unemployment)"),
    },
  ];

  const handleActivityTypeChange = (key: string | number | null) => {
    if (key === null) return;

    const newActivityType = key as GapActivityType;
    setActivityType(newActivityType);

    if (!isGapStatementActivity(newActivityType, includesEducation)) {
      onReasonChange("");
    }
  };

  const handleReasonChange = (value: string) => {
    onReasonChange(value);

    if (value) {
      if (includesEducation || activityType !== "STUDYING") {
        setActivityType("OTHER");
      }
    }
  };

  return (
    <Stack gap="space-4">
      <Select
        isInvalid={false}
        isRequired
        label={t("What were you doing during this time?")}
        placeholder={t("Please select")}
        selectedKey={activityType}
        onSelectionChange={handleActivityTypeChange}
        testID={`${testID}-activity-select`}
      >
        {gapActivityOptions.map((option) => (
          <SelectItem key={option.value} id={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </Select>

      {activityType === "WORKING" && (
        <SectionMessage
          type="info"
          sectionTitle={t("This should be added as employment")}
          icon="info"
        >
          <Trans>
            Add this to your employment history so it can be verified.
            <br />
            <Link
              onPress={() => {
                onNavigateToSection("employment-history");
              }}
              textColor="text-info"
              testID={`${testID}-add-employment-link`}
            >
              {t("Add employment")}
            </Link>
          </Trans>
        </SectionMessage>
      )}

      {activityType === "STUDYING" && includesEducation && (
        <SectionMessage
          type="info"
          sectionTitle={t("This should be added as education")}
          icon="info"
        >
          <Trans>
            Add this to your education history so it can be verified.
            <br />
            <Link
              onPress={() => {
                onNavigateToSection("education-history");
              }}
              textColor="text-info"
              testID={`${testID}-add-education-link`}
            >
              {t("Add education")}
            </Link>
          </Trans>
        </SectionMessage>
      )}

      {isGapStatementActivity(activityType, includesEducation) && (
        <>
          <Divider />
          <Stack gap="space-4">
            <TextArea
              isRequired
              label={t("Gap statement")}
              value={reason}
              onChange={handleReasonChange}
              maxLength={1000}
              errors={errors}
              testID={`${testID}-reason-textarea`}
              placeholder={t(
                "Please describe what you were doing during this period"
              )}
            />
          </Stack>
        </>
      )}
    </Stack>
  );
}
